/**
 * Live use-case directory data. Source of truth: "Open - Offering - Use cases"
 * internal sheet. Impact strings are split into chips on the card, so keep
 * each impact item short and self-contained.
 */

export type UseCaseProduct =
  | 'Inbound'
  | 'Outbound'
  | 'Customer Insights'
  | 'Agentic Workflows';

export type UseCasePhase = 'Pre-sale' | 'Sale' | 'Post-sale';

export type UseCaseChannel =
  | 'Chat'
  | 'Email'
  | 'Voice'
  | 'Social'
  | 'Instagram'
  | 'Messenger'
  | 'SMS'
  | 'WhatsApp'
  | 'All channels';

export interface UseCase {
  id: string;
  product: UseCaseProduct;
  family: string;
  name: string;
  description: string;
  impact: string[];
  phases: UseCasePhase[];
  channels: UseCaseChannel[];
}

export const USE_CASE_PRODUCTS: UseCaseProduct[] = [
  'Inbound',
  'Outbound',
  'Customer Insights',
  'Agentic Workflows',
];

export const USE_CASES: UseCase[] = [
  /* ----------------------------- Inbound ----------------------------- */
  {
    id: 'helpdesk-ai-layer',
    product: 'Inbound',
    family: 'AI Layer on top of Existing Helpdesk',
    name: 'Automate inbound support within your current helpdesk',
    description:
      'Open sits on top of your existing helpdesk (Zendesk, Intercom, Salesforce, Freshdesk, HubSpot, and more) and resolves 60-80% of inbound queries automatically across chat, email, voice, and social. No migration needed.',
    impact: [
      '60-80% automation rate',
      'CSAT improvement 10+ pp',
      'Response time from hours to seconds',
    ],
    phases: ['Post-sale'],
    channels: ['Chat', 'Email', 'Voice', 'Social'],
  },
  {
    id: 'ai-human-handoffs',
    product: 'Inbound',
    family: 'AI Layer on top of Existing Helpdesk',
    name: 'Seamless AI-to-human handoffs',
    description:
      'Open hands off to human agents invisibly in the same thread with full conversation context. The customer never notices the switch.',
    impact: ['Zero context loss on escalation', 'Avg handle time drops 30-40%'],
    phases: ['Post-sale'],
    channels: ['Chat', 'Email', 'Voice', 'Social'],
  },
  {
    id: 'smart-routing',
    product: 'Inbound',
    family: 'Smart Routing',
    name: 'Skill-based routing to human agents',
    description:
      'AI classifies intent and routes to the best-skilled human agent with full context. Priority-based routing for VIP customers.',
    impact: [
      'First-contact resolution improvement',
      'Reduced internal transfers',
      'Faster resolution for complex cases',
    ],
    phases: ['Post-sale'],
    channels: ['All channels'],
  },
  {
    id: 'agent-assist-copilot',
    product: 'Inbound',
    family: 'Assist Mode',
    name: 'Real-time agent assist copilot',
    description:
      'While a human agent is on a call or chat, AI listens and reads silently in the background, instantly surfacing relevant KB articles, policy updates, and the next best action directly on their screen.',
    impact: [
      '30% reduction in agent training and ramp time',
      'Higher FCR',
      'Strict compliance on every call',
    ],
    phases: ['Pre-sale', 'Post-sale'],
    channels: ['Chat', 'Email', 'Social'],
  },
  {
    id: 'multi-language-support',
    product: 'Inbound',
    family: 'Multi-Language',
    name: '24/7 multi-language support',
    description:
      'One AI agent speaks 60+ languages with native pronunciation. Available 24/7 across all channels. No per-language staffing needed.',
    impact: [
      '24/7 coverage in all markets',
      'No language-based SLA gaps',
      'Consistent quality in every language',
    ],
    phases: ['Post-sale'],
    channels: ['All channels'],
  },
  {
    id: 'knowledge-gap-detection',
    product: 'Inbound',
    family: 'Knowledge Management',
    name: 'Knowledge gap detection',
    description:
      'Open tracks which knowledge sources cause AI-to-human escalations. Clusters gaps by impact. Suggests exact content to add or fix. Your knowledge base maintains itself.',
    impact: [
      'Continuous KB improvement',
      'Higher automation rate over time',
      'Fewer escalations caused by bad docs',
    ],
    phases: ['Post-sale'],
    channels: ['All channels'],
  },
  {
    id: 'automate-phone-support',
    product: 'Inbound',
    family: 'AI Voice Support',
    name: 'Automate phone support',
    description:
      'AI Voice handles calls with natural human-like conversations. Supports 6+ languages with native pronunciation. Handles up to 500 concurrent calls, no hold times ever.',
    impact: [
      'Zero hold time',
      '24/7 phone coverage',
      'Cost per call drops 70%+',
      'Supports 100+ countries',
    ],
    phases: ['Post-sale'],
    channels: ['Voice'],
  },
  {
    id: 'custom-voice-branding',
    product: 'Inbound',
    family: 'AI Voice Support',
    name: 'Custom voice branding',
    description:
      "Clone your brand voice by using actual voices of your team, or create custom voices. Customers won't know they're talking to AI.",
    impact: [
      'Brand-consistent phone experience',
      'Higher caller satisfaction',
    ],
    phases: ['Post-sale'],
    channels: ['Voice'],
  },
  {
    id: 'social-media-automation',
    product: 'Inbound',
    family: 'AI Social Support',
    name: 'Social media (Instagram and Messenger) automation',
    description:
      'AI responds to Instagram DMs and Facebook Messenger instantly. Handles story mentions and comment replies. Can escalate to humans seamlessly.',
    impact: [
      'Instant social response',
      'No missed DMs',
      'Social CSAT improvement',
    ],
    phases: ['Pre-sale', 'Post-sale'],
    channels: ['Instagram', 'Messenger'],
  },
  {
    id: 'comment-mention-monitoring',
    product: 'Inbound',
    family: 'AI Social Support',
    name: 'Comment and mention monitoring',
    description:
      'AI monitors post comments and responds automatically, handing off sensitive issues to a human agent.',
    impact: [
      '100% comment coverage',
      'Faster public issue resolution',
      'Brand protection',
    ],
    phases: ['Pre-sale', 'Post-sale'],
    channels: ['Instagram', 'Messenger'],
  },
  {
    id: 'automate-email-inbox',
    product: 'Inbound',
    family: 'AI Email Support',
    name: 'Automate email inbox',
    description:
      'AI responds from your domain within minutes. Reads attachments (screenshots, PDFs, invoices). Remembers full email history so customers never repeat themselves.',
    impact: [
      'Email response time from 24h to minutes',
      '60-80% email automation',
    ],
    phases: ['Post-sale'],
    channels: ['Email'],
  },
  {
    id: 'multi-inbox-management',
    product: 'Inbound',
    family: 'AI Email Support',
    name: 'Multi-inbox management',
    description:
      'One dashboard for all inboxes with smart routing. AI handles each inbox with different knowledge and tone. Automatic routing to the right team.',
    impact: [
      'Unified inbox management',
      'Consistent quality across all email addresses',
    ],
    phases: ['Post-sale'],
    channels: ['Email'],
  },
  {
    id: 'secure-pii-verification',
    product: 'Inbound',
    family: 'Security',
    name: 'Secure PII authentication and verification',
    description:
      'AI handles secure upfront verification (account numbers, PINs) before passing to a live agent. Masks PII data so human agents never see or hear sensitive credit card or social security numbers.',
    impact: ['100% compliance with PCI/HIPAA standards'],
    phases: ['Pre-sale', 'Post-sale'],
    channels: ['All channels'],
  },
  {
    id: 'website-chat-automation',
    product: 'Inbound',
    family: 'AI Chat Support',
    name: 'Website chat automation',
    description:
      'AI Chat widget on your website provides instant answers with rich media (images, videos, buttons, carousels). Handles returns and account changes, not just FAQs. Proactive chat triggers based on user behavior.',
    impact: [
      '50-70% chat automation',
      '24/7 coverage',
      'No more lost visitors',
    ],
    phases: ['Pre-sale', 'Post-sale'],
    channels: ['Chat'],
  },

  /* ----------------------------- Outbound ---------------------------- */
  {
    id: 'lead-qualification-ai',
    product: 'Outbound',
    family: 'Sales Outreach',
    name: 'Lead qualification via AI',
    description:
      'AI calls or chats with inbound leads to qualify them against your criteria. Updates your CRM automatically. Only passes hot leads to sales reps.',
    impact: [
      '30-50% more qualified pipeline',
      '0 time wasted on unqualified leads',
      'Instant lead response',
    ],
    phases: ['Pre-sale'],
    channels: ['Voice', 'Chat'],
  },
  {
    id: 'reminder-calls',
    product: 'Outbound',
    family: 'AI Voice Outbound',
    name: 'Reminder calls (appointments, payments, renewals)',
    description:
      'AI makes personalized reminder calls at scale. Confirms appointments, reminds about upcoming payments, and alerts customers about expiring subscriptions.',
    impact: [
      '20-30% reduction in no-shows',
      'Improved payment collection rates',
      'Lower churn',
    ],
    phases: ['Sale', 'Post-sale'],
    channels: ['Voice'],
  },
  {
    id: 'upsell-calls',
    product: 'Outbound',
    family: 'AI Voice Outbound',
    name: 'Upsell calls before key events',
    description:
      'AI calls customers 24-48h before a key event with a personalized upsell offer. Natural conversation with objection handling.',
    impact: [
      '5-15% upsell conversion rate',
      'Revenue per customer increase',
      'No additional headcount needed',
    ],
    phases: ['Pre-sale', 'Sale'],
    channels: ['Voice'],
  },
  {
    id: 'follow-up-calls',
    product: 'Outbound',
    family: 'AI Voice Outbound',
    name: 'Post-interaction follow-up calls',
    description:
      'AI calls customers after key interactions to check satisfaction and address any remaining issues. Feeds insights back into your systems.',
    impact: [
      'Higher NPS/CSAT',
      'Early detection of unresolved issues',
      'Stronger customer relationships',
    ],
    phases: ['Post-sale'],
    channels: ['Voice'],
  },
  {
    id: 'collection-reminders',
    product: 'Outbound',
    family: 'AI Voice Outbound',
    name: 'Collection reminders via voice',
    description:
      'AI calls with polite and professional payment reminders. Escalates to humans only for disputes. Handles payment plan discussions.',
    impact: [
      '15-25% improvement in collection rates',
      'Freed agent time',
      'Consistent professional tone',
    ],
    phases: ['Post-sale'],
    channels: ['Voice'],
  },
  {
    id: 'multi-channel-sequences',
    product: 'Outbound',
    family: 'AI Outbound Sequences',
    name: 'Multi-channel outreach sequences',
    description:
      'Open sequences outbound communication across voice, email, SMS, and WhatsApp. Conditional logic adapts the sequence based on customer responses.',
    impact: [
      'Higher contact rates',
      'Consistent follow-up execution',
      'Zero dropped sequences',
    ],
    phases: ['Pre-sale', 'Sale', 'Post-sale'],
    channels: ['Voice', 'Email', 'SMS', 'WhatsApp'],
  },
  {
    id: 'nps-csat-voice-surveys',
    product: 'Outbound',
    family: 'AI Outbound Sequences',
    name: 'NPS/CSAT survey collection via voice',
    description:
      'AI calls customers for NPS/CSAT surveys with natural conversations. Gets deeper qualitative feedback than forms ever could.',
    impact: [
      '3-5x higher response rate vs email surveys',
      'Richer qualitative data',
      'Real-time sentiment tracking',
    ],
    phases: ['Post-sale'],
    channels: ['Voice'],
  },
  {
    id: 'win-back-campaigns',
    product: 'Outbound',
    family: 'AI Outbound Sequences',
    name: 'Win-back campaigns for churning customers',
    description:
      'AI contacts at-risk or recently churned customers via voice, email, or SMS with personalized retention offers. Captures churn reasons.',
    impact: [
      '10-20% win-back rate',
      'Structured churn reason data for product teams',
      'Revenue recovery',
    ],
    phases: ['Post-sale'],
    channels: ['Voice', 'Email', 'SMS'],
  },

  /* ------------------------- Customer Insights ------------------------ */
  {
    id: 'root-cause-analysis',
    product: 'Customer Insights',
    family: 'Root Cause Analysis',
    name: 'Understand what drives support volume',
    description:
      'AI analyzes every conversation to identify root causes, not symptoms. Is it a confusing UI, a broken feature, missing documentation, or a process gap? You get clarity.',
    impact: [
      'Address root causes to permanently reduce ticket volume',
      'Data-backed ops decisions',
    ],
    phases: ['Post-sale'],
    channels: ['All channels'],
  },
  {
    id: 'kb-quality-scoring',
    product: 'Customer Insights',
    family: 'Root Cause Analysis',
    name: 'Knowledge base quality scoring',
    description:
      'Open tracks which KB articles lead to AI escalations and customer confusion. Ranks articles by quality and suggests improvements.',
    impact: [
      'Continuous KB improvement',
      'Higher AI accuracy',
      'Fewer unnecessary escalations',
    ],
    phases: ['Post-sale'],
    channels: ['All channels'],
  },
  {
    id: 'ai-agent-qa',
    product: 'Customer Insights',
    family: 'QA & Performance',
    name: 'AI agent quality assurance',
    description:
      'Built-in QA tools let you audit AI responses, track accuracy, confidence levels, and customer satisfaction per response. Identify areas for improvement.',
    impact: [
      'Confidence thresholds on every AI response',
      'Continuous improvement loop',
      'Brand-safe responses',
    ],
    phases: ['Post-sale'],
    channels: ['All channels'],
  },
  {
    id: 'feature-feedback-detection',
    product: 'Customer Insights',
    family: 'QA & Performance',
    name: 'New feature feedback detection',
    description:
      'AI detects conversations about new features, clusters feedback (positive, negative, confused), and delivers structured reports to product teams.',
    impact: [
      'Instant post-launch feedback',
      'Feature adoption tracking',
      'Iterate faster on real customer input',
    ],
    phases: ['Post-sale'],
    channels: ['All channels'],
  },
  {
    id: 'product-roadmap-conversations',
    product: 'Customer Insights',
    family: 'Product Intelligence',
    name: 'Product roadmap from customer conversations',
    description:
      'Customer Insights analyzes every conversation across all channels. Clusters feature requests by volume and impact. Auto-generates Jira tickets with full context, affected user count, and priority.',
    impact: [
      'Product decisions backed by real customer data',
      '10x faster insight-to-ticket pipeline',
      'Nothing falls through the cracks',
    ],
    phases: ['Post-sale'],
    channels: ['All channels'],
  },
  {
    id: 'bug-detection-prioritization',
    product: 'Customer Insights',
    family: 'Product Intelligence',
    name: 'Bug detection and prioritization',
    description:
      'AI identifies bug reports in conversations, clusters similar reports, quantifies the number of affected users, and creates prioritized Jira tickets with full reproduction context.',
    impact: [
      'Faster bug detection',
      'Impact-based prioritization',
      'No more "we didn\'t know" moments',
    ],
    phases: ['Post-sale'],
    channels: ['All channels'],
  },
  {
    id: 'cross-channel-sentiment',
    product: 'Customer Insights',
    family: 'Customer Sentiment',
    name: 'Cross-channel sentiment analysis',
    description:
      'Customer Insights analyzes sentiment in real time across every channel. Tracks trends over time. Alerts you to sudden drops before they become crises.',
    impact: [
      'Real-time sentiment dashboard',
      'Early warning system',
      'Channel-specific CSAT tracking',
    ],
    phases: ['Post-sale'],
    channels: ['All channels'],
  },
  {
    id: 'at-risk-identification',
    product: 'Customer Insights',
    family: 'Customer Sentiment',
    name: 'At-risk customer identification',
    description:
      'AI flags customers showing signs of frustration or churn risk based on conversation patterns. Triggers proactive outreach or account manager alerts.',
    impact: [
      'Reduce churn by 10-20%',
      'Proactive intervention',
      'Save high-value accounts',
    ],
    phases: ['Post-sale'],
    channels: ['All channels'],
  },
  {
    id: 'competitor-mention-tracking',
    product: 'Customer Insights',
    family: 'Competitive Intelligence',
    name: 'Competitor mention tracking',
    description:
      'AI detects competitor mentions across all support channels. Clusters themes (pricing, features, UX) to show exactly where you win or lose against each competitor.',
    impact: [
      'Real-time competitive intelligence',
      'Product positioning insights',
      'Sales enablement data',
    ],
    phases: ['Pre-sale', 'Post-sale'],
    channels: ['All channels'],
  },

  /* ------------------------- Agentic Workflows ------------------------ */
  {
    id: 'multi-party-coordination',
    product: 'Agentic Workflows',
    family: 'Complex Communication',
    name: 'Multi-party coordination workflows',
    description:
      'Agentic Workflows orchestrate multi-party communication automatically. Call party A, if they confirm, email party B, then update party C and the CRM. All autonomous.',
    impact: [
      'Hours of manual coordination per case reduced to zero',
      'Zero dropped handoffs',
      'Perfect audit trail',
    ],
    phases: ['Post-sale'],
    channels: ['Voice', 'Email', 'SMS', 'WhatsApp'],
  },
  {
    id: 'review-score-improvement',
    product: 'Agentic Workflows',
    family: 'Revenue Workflows',
    name: 'Systematic review and Trustpilot score improvement',
    description:
      'Workflow identifies satisfied customers (based on CSAT and conversation sentiment), then triggers a personalized review request via their preferred channel. Routes unhappy customers to support first.',
    impact: [
      '0.5-1.0 star improvement over 3-6 months',
      'Higher review volume',
      'Unhappy customers intercepted before public review',
    ],
    phases: ['Post-sale'],
    channels: ['Voice', 'Email', 'SMS', 'WhatsApp'],
  },
  {
    id: 'renewal-upgrade-workflows',
    product: 'Agentic Workflows',
    family: 'Revenue Workflows',
    name: 'Subscription renewal and upgrade workflows',
    description:
      'Workflow triggers X days before renewal: AI call to discuss experience, offer upgrade or discount, confirm renewal. Adapts sequence based on customer response and history.',
    impact: [
      '15-25% churn reduction on renewals',
      'Increased upgrade conversion',
      'Predictable revenue',
    ],
    phases: ['Sale', 'Post-sale'],
    channels: ['Voice', 'Email', 'SMS'],
  },
  {
    id: 'lead-qualification-crm-workflow',
    product: 'Agentic Workflows',
    family: 'Operations Automation',
    name: 'Lead qualification and CRM update workflow',
    description:
      'Agentic Workflow qualifies leads via AI conversation (voice or chat), scores them against your criteria, updates your CRM with structured data, and routes hot leads to sales instantly.',
    impact: [
      '50% faster lead-to-response time',
      '100% CRM data completeness',
      'Sales reps only see qualified leads',
    ],
    phases: ['Pre-sale'],
    channels: ['Voice', 'Chat'],
  },
  {
    id: 'appointment-scheduling-workflow',
    product: 'Agentic Workflows',
    family: 'Operations Automation',
    name: 'Appointment scheduling and reminders workflow',
    description:
      'Workflow handles end-to-end: AI books the appointment via conversation, sends calendar invite, makes reminder call 24h before, handles rescheduling requests automatically.',
    impact: [
      '30% fewer no-shows',
      'Zero manual scheduling effort',
      'Seamless customer experience',
    ],
    phases: ['Pre-sale', 'Sale'],
    channels: ['Voice', 'Email', 'SMS'],
  },
];
